import { StatsEmitterServiceInterface } from '../../service/stats-emitter/service.interface';
import { StatsContextInterface } from './context.interface';
import { StatsTrackerInterface } from './tracker.interface';
import { StatsDataPropertyInterface } from './data/property.interface';
import { StatsDataAgentInterface } from './data/agent.interface';
import { StatsDataBrokerInterface } from './data/broker.interface';
import { StatsEmitterEvent } from './emitter.event';
import { StatsDataUserInterface } from './data/user.interface';

export class StatsObserver {
  /**
   *
   * @param statsTracker
   */
  constructor(protected statsTracker: StatsTrackerInterface) {}

  /**
   * Attach event listeners to the stats emitter service
   *
   * @param statsEmitterService
   */
  public attachListeners(statsEmitterService: StatsEmitterServiceInterface): void {
    const eventEmitter = statsEmitterService.getEventEmitter();

    eventEmitter.addListener(StatsEmitterEvent.pageView, this.onPageView.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.homePage, this.onHomePage.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.propertyPage, this.onPropertyPage.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.propertySerp, this.onPropertySerp.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.propertyImpression, this.onPropertyImpression.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.propertyLoad, this.onPropertyLoad.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.propertyLeadClick, this.onPropertyLeadClick.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.propertyLeadSend, this.onPropertyLeadSend.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.propertyClick, this.onPropertyClick.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.propertySave, this.onPropertySave.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.propertyUnsave, this.onPropertyUnsave.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.propertyImageBrowse, this.onPropertyImageBrowse.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.agentHome, this.onAgentHome.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.agentPage, this.onAgentPage.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.agentSerp, this.onAgentSerp.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.agentImpression, this.onAgentImpression.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.agentLoad, this.onAgentLoad.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.agentLeadClick, this.onAgentLeadClick.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.agentLeadSend, this.onAgentLeadSend.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.agentClick, this.onAgentClick.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.brokerHome, this.onBrokerHome.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.brokerPage, this.onBrokerPage.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.brokerSerp, this.onBrokerSerp.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.brokerImpression, this.onBrokerImpression.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.brokerLoad, this.onBrokerLoad.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.brokerLeadClick, this.onBrokerLeadClick.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.brokerLeadSend, this.onBrokerLeadSend.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.brokerClick, this.onBrokerClick.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.userPage, this.onUserPage.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.userSignIn, this.onUserSignIn.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.userSignOut, this.onUserSignOut.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.userRegister, this.onUserRegister.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.managerClick, this.onManagerClick.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.ctaClick, this.onCtaClick.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.errorPage, this.onErrorPage.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.myAccountPage, this.onMyAccountPage.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.buyVsRentCalculatorPage, this.onBuyVsRentCalculatorPage.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.propertyReadmoreClick, this.onPropertyReadmoreClick.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.propertyLeadSendWhatsapp, this.onPropertyLeadSendWhatsapp.bind(this));
    eventEmitter.addListener(StatsEmitterEvent.propertyLeadClickWhatsapp, this.onPropertyLeadClickWhatsapp.bind(this));
  }

  /**
   * Emitted a page view
   *
   * @param context
   */
  protected onPageView(context: StatsContextInterface): void {
    this.statsTracker.pageView(context);
  }

  /**
   * Emitted a home page
   *
   * @param context
   */
  protected onHomePage(context: StatsContextInterface): void {
    this.statsTracker.homePage(context);
  }

  /**
   * Emitted a property page
   *
   * @param rawData
   * @param context
   */
  protected onPropertyPage(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    this.statsTracker.propertyPage(rawData);
  }

  /**
   * Emitted a property SERP
   *
   * @param context
   */
  protected onPropertySerp(context: StatsContextInterface): void {
    this.statsTracker.propertySerp(context);
  }

  /**
   * Emitted a property impression
   *
   * @param rawData
   * @param context
   */
  protected onPropertyImpression(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    this.statsTracker.propertyImpression(rawData);
  }

  /**
   * Emitted a property load
   *
   * @param rawData
   * @param context
   */
  protected onPropertyLoad(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    this.statsTracker.propertyLoad(rawData);
  }

  /**
   * Emitted a property lead click
   *
   * @param rawData
   * @param context
   */
  protected onPropertyLeadClick(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    this.statsTracker.propertyLeadClick(rawData);
  }

  /**
   * Emitted a property lead send
   *
   * @param rawData
   * @param context
   */
  protected onPropertyLeadSend(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    this.statsTracker.propertyLeadSend(rawData);
  }

  /**
   * Emitted a property click
   *
   * @param rawData
   * @param context
   */
  protected onPropertyClick(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    this.statsTracker.propertyClick(rawData);
  }

  /**
   * Emitted a property save
   *
   * @param rawData
   * @param context
   */
  protected onPropertySave(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    this.statsTracker.propertySave(rawData);
  }

  /**
   * Emitted a property unsave
   *
   * @param rawData
   * @param context
   */
  protected onPropertyUnsave(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    this.statsTracker.propertyUnsave(rawData);
  }

  /**
   * Emitted a property image browsed
   *
   * @param rawData
   * @param context
   */
  protected onPropertyImageBrowse(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    this.statsTracker.propertyImageBrowse(rawData);
  }

  /**
   * Emitted a agent home
   *
   * @param context
   */
  protected onAgentHome(context: StatsContextInterface): void {
    this.statsTracker.agentHome(context);
  }

  /**
   * Emitted a agent page
   *
   * @param rawData
   * @param context
   */
  protected onAgentPage(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    this.statsTracker.agentPage(rawData);
  }

  /**
   * Emitted a agent SERP
   *
   * @param context
   */
  protected onAgentSerp(context: StatsContextInterface): void {
    this.statsTracker.agentSerp(context);
  }

  /**
   * Emitted a agent impression
   *
   * @param rawData
   * @param context
   */
  protected onAgentImpression(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    this.statsTracker.agentImpression(rawData);
  }

  /**
   * Emitted a agent load
   *
   * @param rawData
   * @param context
   */
  protected onAgentLoad(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    this.statsTracker.agentLoad(rawData);
  }

  /**
   * Emitted a agent lead click
   *
   * @param rawData
   * @param context
   */
  protected onAgentLeadClick(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    this.statsTracker.agentLeadClick(rawData);
  }

  /**
   * Emitted a agent lead send
   *
   * @param rawData
   * @param context
   */
  protected onAgentLeadSend(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    this.statsTracker.agentLeadSend(rawData);
  }

  /**
   * Emitted a agent click
   *
   * @param rawData
   * @param context
   */
  protected onAgentClick(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    this.statsTracker.agentClick(rawData);
  }

  /**
   * Emitted a broker home
   *
   * @param context
   */
  protected onBrokerHome(context: StatsContextInterface): void {
    this.statsTracker.brokerHome(context);
  }

  /**
   * Emitted a broker page
   *
   * @param rawData
   * @param context
   */
  protected onBrokerPage(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    this.statsTracker.brokerPage(rawData);
  }

  /**
   * Emitted a broker SERP
   *
   * @param context
   */
  protected onBrokerSerp(context: StatsContextInterface): void {
    this.statsTracker.brokerSerp(context);
  }

  /**
   * Emitted a broker impression
   *
   * @param rawData
   * @param context
   */
  protected onBrokerImpression(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    this.statsTracker.brokerImpression(rawData);
  }

  /**
   * Emitted a broker load
   *
   * @param rawData
   * @param context
   */
  protected onBrokerLoad(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    this.statsTracker.brokerLoad(rawData);
  }

  /**
   * Emitted a broker lead click
   *
   * @param rawData
   * @param context
   */
  protected onBrokerLeadClick(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    this.statsTracker.brokerLeadClick(rawData);
  }

  /**
   * Emitted a broker lead send
   *
   * @param rawData
   * @param context
   */
  protected onBrokerLeadSend(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    this.statsTracker.brokerLeadSend(rawData);
  }

  /**
   * Emitted a broker click
   *
   * @param rawData
   * @param context
   */
  protected onBrokerClick(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    this.statsTracker.brokerClick(rawData);
  }

  /**
   * Emitted a user page
   *
   * @param context
   */
  protected onUserPage(context: StatsContextInterface): void {
    this.statsTracker.userPage(context);
  }

  /**
   * Emitted a user sign in
   *
   * @param rawData
   * @param context
   */
  protected onUserSignIn(rawData: StatsDataUserInterface, context: StatsContextInterface): void {
    this.statsTracker.userSignIn(rawData);
  }

  /**
   * Emitted a user sign out
   *
   * @param rawData
   * @param context
   */
  protected onUserSignOut(rawData: StatsDataUserInterface, context: StatsContextInterface): void {
    this.statsTracker.userSignOut(rawData);
  }

  /**
   * Emitted a user register
   *
   * @param rawData
   * @param context
   */
  protected onUserRegister(rawData: StatsDataUserInterface, context: StatsContextInterface): void {
    this.statsTracker.userRegister(rawData);
  }

  /**
   * Emitted a manager click
   *
   * @param category
   * @param actionName
   */
  protected onManagerClick(category: string, actionName: string): void {
    this.statsTracker.managerClick(category, actionName);
  }

  /**
   * Emitted a CTA click
   *
   * @param context
   */
  protected onCtaClick(context: StatsContextInterface): void {
    this.statsTracker.ctaClick(context);
  }

  /**
   * Emitted an error page
   *
   * @param context
   */
  protected onErrorPage(context: StatsContextInterface): void {
    this.statsTracker.errorPage(context);
  }

  /**
   * Emitted an error page
   *
   * @param context
   */
  protected onMyAccountPage(context: StatsContextInterface): void {
    this.statsTracker.myAccountPage(context);
  }

  /**
   * Emitted a home page
   *
   * @param context
   */
  protected onBuyVsRentCalculatorPage(context: StatsContextInterface): void {
    this.statsTracker.buyVsRentCalculatorPage(context);
  }

  /**
   * Emitted property description read more click
   *
   * @param context
   */
  protected onPropertyReadmoreClick(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    this.statsTracker.propertyReadmoreClick(context);
  }

  /**
   * Emitted a property lead click
   * It will track GTM only
   *
   * @param rawData
   * @param context
   */
  protected onPropertyLeadClickWhatsapp(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    this.statsTracker.propertyLeadClickWhatsapp(rawData);
  }

  /**
   * Emitted a property lead send
   * It will track snowplow/snowplow2 only
   *
   * @param rawData
   * @param context
   */
  protected onPropertyLeadSendWhatsapp(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    this.statsTracker.propertyLeadSendWhatsapp(rawData);
  }
}
